






























































































import { computed, defineComponent, ref, watch } from "@vue/composition-api";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { Subject } from "@scrile/api-provider/dist/api/SubjectProvider";
import { TipSubjectType } from "@scrile/api-provider/dist/api/TipProvider";
import { UserVerificationStatus } from "@scrile/api-provider/dist/api/UserVerificationProvider";
import { VaultFileUsageSubjectTypeEnum } from "@scrile/api-provider/dist/api/VaultsProvider";
import useAudioRecording from "@/hooks/useAudioRecording";
import useAuthUser from "@/hooks/useAuthUser";
import useVaultView from "@/hooks/useVaultView";
import emitter from "@/lib/emitter";
import { isFile } from "@/lib/fileHelpers";
import { $t } from "@/lib/i18nHelpers";
import config from "@/config";
import { AttachmentFile, InputAttachmentFile } from "@/types";
import { EVENTS } from "@/const";
import AttachmentFilesPreview from "@/components/AttachmentFilesPreview";
import AttachmentPriceSetting from "@/components/AttachmentPriceSetting";
import AudioRecordingView from "@/components/AudioRecordingView";
import BaseIconButton from "@/components/BaseIconButton";
import BaseInputFileWithPreview from "@/components/BaseInputFileWithPreview";
import BaseSendMessage from "@/components/BaseSendMessage";
import TipController from "@/components/TipController";
import TipIcon from "@/components/TipIcon";
import VaultModal from "@/components/VaultModal";
import AudioRecordingDuration from "./AudioRecordingDuration.vue";

export default defineComponent({
  name: "MessageSendWithAttachmentView",
  components: {
    AudioRecordingDuration,
    BaseInputFileWithPreview,
    VaultModal,
    BaseSendMessage,
    BaseIconButton,
    AttachmentFilesPreview,
    AttachmentPriceSetting,
    TipController,
    TipIcon,
    AudioRecordingView,
  },
  props: {
    showAi: { type: Boolean, default: false },
    sending: { type: Boolean, required: true },
    packageList: { type: Array as () => PaymentPackage[], required: true },
    files: { type: Array as () => AttachmentFile[], default: () => [] },
    attachmentPriceId: { type: String, required: true },
    recipientIds: { type: Array as () => string[], required: true },
    threadId: { type: String, default: "0" },
    uploadingAttachment: { type: Boolean, required: true },
    isIncomingMessagesAllow: { type: Boolean, default: false },
    priceAmount: { type: String },
    predefinedText: { type: String, default: "" },
    allowedPublicFilesCount: { type: Number, default: 0 },
    allowedPrivateFilesCount: { type: Number, default: 1 },
  },
  setup(props, { emit }) {
    const { userVerificationUpgradeStatus } = useAuthUser();
    const { showPrivateVault, showPublicVault, isVaultAvailable, showVaultOrNative } = useVaultView();
    const {
      isAudioRecordingAvailable,
      recording: isAudioRecording,
      durationMs,
      audioFile,
      startRecord,
      stopRecord,
    } = useAudioRecording();

    const value = ref<string>("");

    const hasText = computed(() => value.value.trim().length > 0);
    const hasAttachment = computed(() => props.files && props.files.length > 0);
    const isNoPrice = computed(() => props.attachmentPriceId === "0");
    const isAudioMessage = computed(() => props.files.length === 1 && props.files[0].type.startsWith("audio"));
    const canTip = computed(
      () =>
        config.featureTippingMessages &&
        !hasText.value &&
        !hasAttachment.value &&
        !isAudioRecording.value &&
        !isAudioMessage.value
    );
    const loadAttachIconColor = computed(() =>
      props.allowedPrivateFilesCount === 0 ? "var(--palette-gray-lighten-6)" : "var(--palette-gray)"
    );
    const tipSubject = computed(() => ({
      subjectId: props.recipientIds[0],
      subjectType: TipSubjectType.USER,
    }));
    const threadSubject = computed<Subject<VaultFileUsageSubjectTypeEnum.MESSAGE_THREAD>>(() => ({
      subjectId: props.threadId,
      subjectType: VaultFileUsageSubjectTypeEnum.MESSAGE_THREAD,
    }));
    const messagePlaceholder = computed(() => {
      if (isAudioRecording.value) return $t("Recording");
      if (props.priceAmount && isAudioMessage.value) return $t("Audio");
      if (props.priceAmount && hasAttachment.value) return $t("Media");
      return $t("Message");
    });

    const handleInput = (v: string) => {
      value.value = v;
      emit("input", v);
    };

    const checkOnApproved = (id: string) => {
      if (
        props.files &&
        props.files.length > 0 &&
        userVerificationUpgradeStatus.value === UserVerificationStatus.VERIFIED
      ) {
        emit("setPrice", id);
        return;
      }
      emit("setPrice", "0");
      emitter.emit(EVENTS.NOTIFICATION_SHOW, { text: "Price setup declined: your account is pending approval" });
    };

    const allowSend = (value: string) => {
      return !props.uploadingAttachment && (hasAttachment.value || value.trim().length > 0);
    };

    const isUploadingAvailable = (access: boolean = false) => {
      return (access && props.allowedPublicFilesCount !== 0) || (!access && props.allowedPrivateFilesCount !== 0);
    };

    const onAddFiles = (showNativeUploader: () => void, publicAccess: boolean): void => {
      if (isUploadingAvailable(publicAccess) && !isAudioRecording.value) {
        showVaultOrNative(showNativeUploader, publicAccess);
      }
    };

    const addAttachment = (files: (AttachmentFile | File)[], publicAccess: boolean) => {
      const fileList: (InputAttachmentFile | AttachmentFile)[] = files.map(file => {
        if (isFile(file)) {
          return { file, publicAccess };
        }
        file.publicAccess = publicAccess;
        return file;
      });
      fileList.length > 0 && emit("addAttachment", fileList);
    };

    watch(isAudioRecording, (newVal, oldVal) => {
      if (!newVal && oldVal && audioFile.value) {
        addAttachment([audioFile.value], true);
      }
      emit(newVal ? "audioRecordingStarted" : "audioRecordingStopped");
    });

    return {
      showPrivateVault,
      showPublicVault,
      value,
      canTip,
      isNoPrice,
      hasAttachment,
      hasText,
      isVaultAvailable,
      messagePlaceholder,
      tipSubject,
      loadAttachIconColor,
      threadSubject,
      isAudioRecording,
      isAudioRecordingAvailable,
      isAudioMessage,
      allowedFileTypes: config.messageAllowedFileTypes,
      durationMs,
      allowSend,
      handleInput,
      checkOnApproved,
      addAttachment,
      onAddFiles,
      startRecord,
      stopRecord,
    };
  },
});
